<script setup lang="ts">
/** Static keyboard shortcut reference. */
</script>

<template>
  <div class="shortcuts-panel">
    <header class="props-header">
      <h2>{{ $t('flow.keyboardShortcuts') }}</h2>
      <p>{{ $t('flow.shortcutsDesc') }}</p>
    </header>

    <div class="props-body">
      <div class="table-wrap" role="region" :aria-label="$t('flow.shortcutsAriaLabel')">
        <table class="shortcuts-table">
          <thead>
            <tr>
              <th scope="col">{{ $t('flow.shortcutsActionCol') }}</th>
              <th scope="col">{{ $t('flow.shortcutsShortcutCol') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr class="group">
              <th colspan="2" scope="rowgroup">{{ $t('flow.shortcutsGroupCanvasTools') }}</th>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsTogglePanMarquee') }}</td>
              <td>
                <span class="keys"><kbd>S</kbd></span>
              </td>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsPanHold') }}</td>
              <td>
                <span class="keys"
                  ><kbd>Space</kbd><span class="plus">+</span> {{ $t('flow.shortcutsDrag') }}</span
                >
              </td>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsAlternatePan') }}</td>
              <td>
                <span class="keys"
                  ><kbd>Alt</kbd><span class="plus">+</span> {{ $t('flow.shortcutsDrag') }}</span
                >
              </td>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsMarqueeSelection') }}</td>
              <td>
                <span class="keys">{{ $t('flow.shortcutsDragOnCanvas') }}</span>
              </td>
            </tr>

            <tr class="group">
              <th colspan="2" scope="rowgroup">{{ $t('flow.shortcutsGroupCreateNodes') }}</th>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsCreatePhase') }}</td>
              <td>
                <span class="keys"><kbd>P</kbd></span>
              </td>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsCreateGroupGate') }}</td>
              <td>
                <span class="keys"><kbd>G</kbd></span>
              </td>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsCreatePullCard') }}</td>
              <td>
                <span class="keys"><kbd>C</kbd></span>
              </td>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsCreateFlipCard') }}</td>
              <td>
                <span class="keys"><kbd>F</kbd></span>
              </td>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsCreateDelay') }}</td>
              <td>
                <span class="keys"><kbd>D</kbd></span>
              </td>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsCreateMathOp') }}</td>
              <td>
                <span class="keys"><kbd>M</kbd></span>
              </td>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsCreateCondition') }}</td>
              <td>
                <span class="keys"><kbd>K</kbd></span>
              </td>
            </tr>

            <tr class="group">
              <th colspan="2" scope="rowgroup">{{ $t('flow.shortcutsGroupSelectionMovement') }}</th>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsToggleNodeInSelection') }}</td>
              <td>
                <span class="keys"
                  ><kbd>Shift</kbd><span class="plus">+</span> {{ $t('flow.shortcutsClick') }}</span
                >
              </td>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsMoveNodes4px') }}</td>
              <td>
                <span class="keys"><kbd>↑</kbd><kbd>↓</kbd><kbd>←</kbd><kbd>→</kbd></span>
              </td>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsMoveNodes20px') }}</td>
              <td>
                <span class="keys"><kbd>Shift</kbd><span class="plus">+</span><kbd>↑↓←→</kbd></span>
              </td>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsDeleteSelected') }}</td>
              <td>
                <span class="keys"><kbd>Backspace</kbd> / <kbd>Delete</kbd></span>
              </td>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsCopySelected') }}</td>
              <td>
                <span class="keys"><kbd>Ctrl</kbd><kbd>C</kbd></span>
              </td>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsCutSelected') }}</td>
              <td>
                <span class="keys"><kbd>Ctrl</kbd><kbd>X</kbd></span>
              </td>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsPaste') }}</td>
              <td>
                <span class="keys"><kbd>Ctrl</kbd><kbd>V</kbd></span>
              </td>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsDuplicateSelected') }}</td>
              <td>
                <span class="keys"><kbd>Ctrl</kbd><kbd>D</kbd></span>
              </td>
            </tr>

            <tr class="group">
              <th colspan="2" scope="rowgroup">{{ $t('flow.shortcutsGroupConnections') }}</th>
            </tr>
            <tr>
              <td>{{ $t('flow.cancelConnectionDrag') }}</td>
              <td>
                <span class="keys"><kbd>Escape</kbd></span>
              </td>
            </tr>

            <tr class="group">
              <th colspan="2" scope="rowgroup">{{ $t('flow.shortcutsGroupHistoryFile') }}</th>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsUndo') }}</td>
              <td>
                <span class="keys"><kbd>Ctrl</kbd><kbd>Z</kbd></span>
              </td>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsRedo') }}</td>
              <td>
                <span class="keys"
                  ><kbd>Ctrl</kbd><kbd>Shift</kbd><kbd>Z</kbd> / <kbd>Ctrl</kbd><kbd>Y</kbd></span
                >
              </td>
            </tr>
            <tr>
              <td>{{ $t('flow.shortcutsSaveFlow') }}</td>
              <td>
                <span class="keys"><kbd>Ctrl</kbd><kbd>S</kbd></span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<style scoped>
.shortcuts-panel {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  gap: 0.5rem;
}

.props-header {
  flex: 0 0 auto;
}

.props-header h2 {
  margin: 0;
  font-size: 1rem;
  line-height: 1.1;
  padding: 0.45rem 0 0.15rem 0;
}

.props-header p {
  margin: 0.2rem 0 0;
  color: #475569;
  font-size: 0.8rem;
}

.props-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: auto;
  scrollbar-gutter: stable;
  padding-right: 0;
}

.table-wrap {
  padding-right: 1.25rem;
}

.shortcuts-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid rgba(148, 163, 184, 0.55);
  border-radius: 0.75rem;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.66);
}

.shortcuts-table thead th {
  text-align: left;
  font-size: 0.75rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #475569;
  background: rgba(238, 242, 255, 0.75);
  border-bottom: 1px solid rgba(148, 163, 184, 0.45);
  padding: 0.55rem 0.65rem;
}

.shortcuts-table tbody td,
.shortcuts-table tbody th {
  padding: 0.5rem 0.65rem;
  font-size: 0.85rem;
  color: #0f172a;
  vertical-align: middle;
}

.shortcuts-table tbody tr + tr td,
.shortcuts-table tbody tr + tr th {
  border-top: 1px solid rgba(148, 163, 184, 0.25);
}

.shortcuts-table tbody tr.group th {
  font-size: 0.8rem;
  font-weight: 750;
  color: #312e81;
  background: rgba(224, 231, 255, 0.7);
  border-top: none;
}

.keys {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  flex-wrap: wrap;
}

.plus {
  color: #64748b;
  font-weight: 700;
  padding: 0 0.05rem;
}

kbd {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.55rem;
  height: 1.55rem;
  padding: 0 0.45rem;
  border: 1px solid #cbd5f5;
  border-bottom-width: 2px;
  border-radius: 0.5rem;
  background: #fff;
  color: #0f172a;
  font-size: 0.78rem;
  font-weight: 650;
  line-height: 1;
}
</style>
